import Controller from '@ember/controller';
import { service } from '@ember/service';
import { isAppUpdateRequired } from 'garaje/utils/check-app-version';
import { action, set, get } from '@ember/object';
import { restartableTask, all, timeout } from 'ember-concurrency';
import updateAndSaveTask from 'garaje/utils/decorators/update-and-save-task';
import { or, reads } from 'macro-decorators';
import { TEST_IDENTIFIER, VACCINE_IDENTIFIER } from 'garaje/models/user-document-template';
import { task, lastValue } from 'ember-concurrency';
import { cached, localCopy } from 'tracked-toolbox';
import { tracked } from '@glimmer/tracking';
import zft from 'garaje/utils/zero-for-tests';

const MINIMUM_IPAD_VERSION_NEEDED = '3.3.0';
const MINIMUM_IPAD_VERSION_NEEDED_FOR_DOCUMENTS = '3.11.0';

@updateAndSaveTask
class VisitorsSettingsVisitorTypesFlowLegalDocumentsIndexController extends Controller {
  @service trial;
  @service metrics;
  @service currentAdmin;
  @service state;
  @service currentLocation;
  @service('user-document') userDocumentService;
  @service abilities;
  @service store;

  @tracked _configurations = [];

  @isAppUpdateRequired('model.ipads', MINIMUM_IPAD_VERSION_NEEDED) isAppUpdateRequired;
  @isAppUpdateRequired('model.ipads', MINIMUM_IPAD_VERSION_NEEDED_FOR_DOCUMENTS) isAppUpdateRequiredForDocuments;

  @lastValue('setupTrackingConfigurations') trackingConfigurations;
  @localCopy('trackingConfigurations.vaccineConfiguration') vaccineConfiguration;
  @localCopy('trackingConfigurations.testConfiguration') testConfiguration;
  @or('testConfiguration.active', 'vaccineConfiguration.active') trackingEnabled;
  @reads('state.currentLocation.nearVisitScreeningEnabled') nearVisitScreeningEnabled;

  get canAccessDocumentUploads() {
    return this.abilities.can('access document-upload');
  }

  get returningVisitorEnabled() {
    return get(this.state.currentLocation?.config, 'beenHereBefore');
  }

  get legalDocumentsEnabled() {
    return this.model.agreementPage.enabled && !!this.state.features?.includesNda;
  }

  @restartableTask
  *updateAgreementPositions(agreements) {
    yield all(
      agreements.map((agreement, index) => {
        set(agreement, 'position', index + 1);
        return agreement.save({ propagable: false });
      }),
    );
  }

  @task
  *setupTrackingConfigurations() {
    if (!get(this.userDocumentService.loadTemplates, 'lastSuccessful.value')) {
      yield this.userDocumentService.loadTemplates.perform();
    }

    let relevantFlow = this.model.flow;

    if (relevantFlow.isGlobalChild) {
      relevantFlow = yield relevantFlow.globalFlow;
    }

    const [vaccineConfiguration, testConfiguration] = yield all([
      this.userDocumentService.getUserDocumentTemplateConfigurationForFlow(relevantFlow, VACCINE_IDENTIFIER),
      this.userDocumentService.getUserDocumentTemplateConfigurationForFlow(relevantFlow, TEST_IDENTIFIER),
    ]);

    return { vaccineConfiguration, testConfiguration };
  }

  @restartableTask
  *searchUsersTask(term) {
    yield timeout(zft(300));
    const re = new RegExp(`.*${term}.*`, 'i');
    const currentContactsIds = get(this.model.flow, 'visitorDocumentContacts').content.mapBy('id');
    const userRoles = yield this.currentLocation.searchUsers.perform({ term });
    const users = yield all(userRoles.map((role) => get(role, 'user')));

    return users
      .uniqBy('id')
      .reject(({ id }) => currentContactsIds.includes(id))
      .filter(({ fullName }) => fullName.match(re));
  }

  @action
  trackFeatureToggled(isEnabled) {
    let eventName = 'Legal Docs - Legal Documents ';
    eventName += isEnabled ? 'Enabled' : 'Disabled';

    this.metrics.trackEvent(eventName, {
      location_id: this.model.tracking.currentLocationId,
    });
  }

  @action
  toggleAgreementEnabled(agreement, value) {
    this.updateAndSaveTask.perform(agreement, 'enabled', value, false);
  }

  get documentUploadPageId() {
    return this.model.flow.belongsTo('documentUploadPage').id();
  }

  get documentUploadPageEnabled() {
    const page = this.model.flow.belongsTo('documentUploadPage').value();
    return page?.enabled ?? false;
  }

  @cached
  get requiredDocuments() {
    const sorted = this._configurations.slice().sort((a, b) => (a.position || 0) - (b.position || 0));
    const docs = [];
    for (const cfg of sorted) {
      const templateId = cfg.belongsTo('userDocumentTemplate').id();
      const template = templateId ? this.store.peekRecord('custom-user-document-template', templateId) : null;
      if (!template) continue;
      const config = cfg.reUploadConfig;
      let reuploadDate;
      if (config?.policy === 'on_date_yearly' && config.month && config.day) {
        const d = new Date(new Date().getFullYear(), config.month - 1, config.day);
        reuploadDate = d.toISOString();
      }
      docs.push({
        id: template.id,
        title: template.title,
        subtitle: template.description,
        reuploadPolicy: config?.policy,
        reuploadDays: config?.days ? String(config.days) : undefined,
        reuploadDate,
        position: cfg.position || docs.length + 1,
        configurationId: cfg.id,
        allowLlmParsing: cfg.allowLlmParsing,
      });
    }
    return docs;
  }

  @task
  *loadDocumentUploadPage() {
    if (!this.canAccessDocumentUploads) return;
    // Clear stale state from previous flow (controllers are singletons)
    this._configurations = [];

    const pageId = this.model.flow.belongsTo('documentUploadPage').id();
    if (!pageId) return;

    // Fetch per-page configurations from the new endpoint
    const adapter = this.store.adapterFor('document-upload-page');
    const response = yield adapter.fetchConfigurations(pageId);

    // Push included templates into the store so peekRecord works in requiredDocuments
    for (const item of response.included ?? []) {
      this.store.pushPayload({ data: item });
    }

    // Push all configurations into the store in one pass, then peek records
    for (const entry of response.data) {
      this.store.pushPayload({ data: entry });
    }
    const configs = response.data
      .map((entry) => this.store.peekRecord('user-document-template-configuration', entry.id))
      .filter(Boolean);
    this._configurations = configs;
  }

  @action
  toggleAgreementPageEnabled(agreementPage, value) {
    this.updateAndSaveTask.perform(agreementPage, 'enabled', value, false);
    this.trackFeatureToggled(value);
  }

  @action
  refreshDocumentList(change) {
    if (change?.action === 'added' && change.configuration) {
      const exists = this._configurations.some((c) => c.id === change.configuration.id);
      if (!exists) {
        this._configurations = [...this._configurations, change.configuration];
      } else {
        // Replace existing configuration (e.g. after update)
        this._configurations = this._configurations.map((c) =>
          c.id === change.configuration.id ? change.configuration : c,
        );
      }
    } else if (change?.action === 'removed') {
      this._configurations = this._configurations.filter((c) => {
        const templateId = c.belongsTo('userDocumentTemplate').id();
        return templateId !== change.id;
      });
    }
  }

  @action
  toggleDocumentUploadEnabled(value) {
    this.model.flow.documentUploadPage.then((page) => {
      if (page) this.updateAndSaveTask.perform(page, 'enabled', value, false);
    });
  }

  @action
  _trackAddLegalDocStarted() {
    this.send('trackAddLegalDocStarted');
  }

  @action
  _trackEditLegalDocStarted() {
    this.send('trackEditLegalDocStarted');
  }
}

export default VisitorsSettingsVisitorTypesFlowLegalDocumentsIndexController;
